'use client';

import { useCallback } from 'react';

import { LINK_NAVIGATION_MODE } from '@/common/constants';
import type { LinkLocation } from '@/components/ui/link-location-context';
import { useRenderingContext, type RenderingContext } from '@/hooks/use-rendering-context';
import { POST_MESSAGE_TYPES } from '@/lib/constants/post-message-types';
import { isInIframe } from '@/lib/utils/dom';

export type LinkNavigationMode = (typeof LINK_NAVIGATION_MODE)[keyof typeof LINK_NAVIGATION_MODE];

export type ResolvedLinkLocation = LinkLocation | 'markdown';

function resolveMode({
  renderingContext,
  linkLocation,
}: {
  renderingContext: RenderingContext;
  linkLocation: ResolvedLinkLocation;
}): LinkNavigationMode {
  if (renderingContext === 'tenant-site' && linkLocation !== 'markdown') {
    return LINK_NAVIGATION_MODE.REPLACE_PARENT;
  }
  return LINK_NAVIGATION_MODE.NEW_TAB;
}

function executeNavigation({ mode, href }: { mode: LinkNavigationMode; href: string }): boolean {
  if (mode === LINK_NAVIGATION_MODE.REPLACE_PARENT) {
    if (isInIframe()) {
      window.parent.postMessage({ type: POST_MESSAGE_TYPES.NAVIGATE_PARENT, url: href }, '*');
      return true;
    }
    window.location.href = href;
    return true;
  }

  if (isInIframe()) {
    window.parent.postMessage({ type: POST_MESSAGE_TYPES.OPEN_IN_NEW_TAB, url: href }, '*');
    return true;
  }

  window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}

export function useIframeNavigation() {
  const renderingContext = useRenderingContext();

  const getNavigationMode = useCallback(
    (linkLocation: ResolvedLinkLocation): LinkNavigationMode =>
      resolveMode({ renderingContext, linkLocation }),
    [renderingContext],
  );

  const navigate = useCallback(
    ({
      href,
      linkLocation,
      forceNewTab = false,
    }: {
      href: string;
      linkLocation: ResolvedLinkLocation;
      forceNewTab?: boolean;
    }): boolean => {
      if (!href) return false;
      const mode = forceNewTab
        ? LINK_NAVIGATION_MODE.NEW_TAB
        : resolveMode({ renderingContext, linkLocation });
      return executeNavigation({ mode, href });
    },
    [renderingContext],
  );

  return { getNavigationMode, navigate };
}
